<script lang="ts">
  import { router } from '../lib/router.svelte';
  import { t } from '../lib/i18n';

  const links = [
    ['/', 'nav.test'],
    ['/historique', 'nav.history'],
    ['/observatoire', 'nav.observatory'],
    ['/methodologie', 'nav.method'],
    ['/partenaires', 'nav.partners'],
  ] as const;

  const systemDark = () => matchMedia('(prefers-color-scheme: dark)').matches;
  let theme = $state<'light' | 'dark'>(
    (document.documentElement.dataset.theme as 'light' | 'dark' | undefined) ?? (systemDark() ? 'dark' : 'light'),
  );
  function setTheme(v: 'light' | 'dark') {
    theme = v;
    document.documentElement.dataset.theme = v;
    try {
      localStorage.setItem('theme', v);
    } catch {
      /* stockage indisponible */
    }
  }
  let open = $state(false);
  $effect(() => {
    void router.path;
    open = false;
  });
  const active = (href: string) => (href === '/' ? router.path === '/' : router.path.startsWith(href));
</script>

<a class="skip" href="#contenu">{t('nav.skip')}</a>
<header>
  <div class="bar">
    <a class="brand" href="/" aria-label="Mesurio, accueil">
      <svg class="logo" viewBox="0 0 36 36" width="34" height="34" aria-hidden="true">
        <defs>
          <linearGradient id="logo-g" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stop-color="var(--ring-a)" />
            <stop offset=".55" stop-color="var(--ring-b)" />
            <stop offset="1" stop-color="var(--ring-c)" />
          </linearGradient>
        </defs>
        <circle cx="18" cy="18" r="14" fill="none" stroke="var(--border)" stroke-width="3" />
        <circle class="arc" cx="18" cy="18" r="14" fill="none" stroke="url(#logo-g)" stroke-width="3" stroke-linecap="round" stroke-dasharray="62 88" transform="rotate(135 18 18)" />
        <circle cx="18" cy="18" r="5" fill="url(#logo-g)" />
        <circle class="dot" cx="18" cy="4" r="2.6" fill="var(--ring-c)" />
      </svg>
      <span class="word">mesur<span class="io">io</span></span>
    </a>

    <div class="theme" role="group" aria-label={t('theme.toggle')}>
      <button aria-pressed={theme === 'light'} onclick={() => setTheme('light')} title="Thème clair">
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"
          ><circle cx="12" cy="12" r="4.2" fill="currentColor" /><g stroke="currentColor" stroke-width="2" stroke-linecap="round"
            ><path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.3 5.3l1.6 1.6M17.1 17.1l1.6 1.6M5.3 18.7l1.6-1.6M17.1 6.9l1.6-1.6" /></g
          ></svg
        >
        <span class="sr-only">Clair</span>
      </button>
      <button aria-pressed={theme === 'dark'} onclick={() => setTheme('dark')} title="Thème sombre">
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" /></svg>
        <span class="sr-only">Sombre</span>
      </button>
    </div>

    <nav aria-label="Navigation principale" class:open id="menu-principal">
      <ul>
        {#each links as [href, key]}
          <li><a {href} aria-current={active(href) ? 'page' : undefined}>{t(key)}</a></li>
        {/each}
      </ul>
    </nav>
    <button class="burger" aria-expanded={open} aria-controls="menu-principal" onclick={() => (open = !open)}>
      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"
        >{#if open}<path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />{:else}<path
            d="M4 7h16M4 12h16M4 17h16"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />{/if}</svg
      >
      <span class="sr-only">Menu</span>
    </button>
  </div>
</header>

<style>
  .skip { position: absolute; left: -999px; top: 8px; background: var(--surface-solid); padding: 0.5rem 1rem; z-index: 20; border-radius: 10px; }
  .skip:focus { left: 8px; }
  header { position: sticky; top: 0; z-index: 10; padding: 12px 12px 0; }
  .bar {
    max-width: 1120px;
    margin: 0 auto;
    padding: 8px 10px 8px 16px;
    display: flex;
    align-items: center;
    gap: 14px;
    background: color-mix(in oklab, var(--surface-solid) 70%, transparent);
    border: 1px solid var(--border);
    border-radius: 999px;
    backdrop-filter: blur(18px) saturate(160%);
    -webkit-backdrop-filter: blur(18px) saturate(160%);
    box-shadow: var(--shadow);
    position: relative;
  }
  .brand { display: inline-flex; align-items: center; gap: 0.55rem; font-family: var(--font-display); font-weight: 700; font-size: 1.4rem; letter-spacing: -0.03em; color: var(--text); text-decoration: none; }
  .io { background: var(--grad); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: var(--primary); }
  .logo .dot { transform-origin: 18px 18px; animation: orbite 6s linear infinite; filter: drop-shadow(0 0 4px var(--ring-c)); }
  .brand:hover .arc { stroke-dasharray: 88 88; transition: stroke-dasharray 0.6s ease; }
  .arc { transition: stroke-dasharray 0.6s ease; }
  @keyframes orbite { to { transform: rotate(360deg); } }
  .theme { display: inline-flex; background: var(--surface-2); border: 1px solid var(--border); border-radius: 999px; padding: 3px; gap: 2px; }
  .theme button { min-height: 32px; width: 38px; padding: 0; border: 0; border-radius: 999px; background: transparent; color: var(--muted); backdrop-filter: none; }
  .theme button:hover { background: transparent; color: var(--text); }
  .theme button[aria-pressed='true'] { background: var(--grad-btn); color: #fff; box-shadow: var(--glow); }
  nav { margin-left: auto; }
  ul { display: flex; gap: 2px; list-style: none; margin: 0; padding: 0; }
  nav a { position: relative; display: block; padding: 0.5rem 0.85rem; border-radius: 999px; color: var(--muted); text-decoration: none; font-weight: 550; }
  nav a:hover { color: var(--text); background: var(--surface-2); }
  nav a[aria-current='page'] { color: var(--text); background: var(--surface-2); }
  nav a[aria-current='page']::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 3px;
    width: 18px;
    height: 3px;
    margin-left: -9px;
    border-radius: 3px;
    background: var(--grad);
    box-shadow: 0 0 10px var(--ring-b);
  }
  .burger { display: none; margin-left: auto; border: 0; background: transparent; padding: 0 8px; color: var(--text); backdrop-filter: none; }
  @media (max-width: 820px) {
    .burger { display: inline-flex; }
    nav {
      display: none;
      position: absolute;
      left: 0;
      right: 0;
      top: calc(100% + 8px);
      background: color-mix(in oklab, var(--surface-solid) 92%, transparent);
      border: 1px solid var(--border);
      border-radius: 22px;
      padding: 8px;
      backdrop-filter: blur(18px);
      box-shadow: var(--shadow);
    }
    nav.open { display: block; animation: monte 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
    ul { flex-direction: column; }
    nav a { font-size: 1.1rem; padding: 0.8rem 1rem; border-radius: 14px; }
    nav a[aria-current='page']::after { left: auto; right: 16px; top: 50%; bottom: auto; margin: -2px 0 0; width: 8px; height: 8px; border-radius: 50%; }
  }
  @media (max-width: 400px) {
    .word { display: none; }
  }
</style>
